import { computed, watch, type MaybeRefOrGetter, toValue } from "vue";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type {
    AdminSatelliteContactSitesListQueryParams,
    CreateSatelliteContactSiteRequest,
    UpdateSatelliteContactSiteRequest,
} from "@shopexperts/legacy-api";

export const SATELLITE_CONTACT_SITES_QUERY_KEY = ["admin", "satellite-contact-sites"] as const;

type SatelliteContactSitesQuery = Omit<AdminSatelliteContactSitesListQueryParams, "cursor">;

export function useSatelliteContactSites(
    query: MaybeRefOrGetter<SatelliteContactSitesQuery | undefined> = {},
) {
    const api = useApi();
    const toaster = useToaster();

    const queryResult = useInfiniteQuery({
        queryKey: computed(() => [
            ...SATELLITE_CONTACT_SITES_QUERY_KEY,
            "list",
            toValue(query) ?? {},
        ]),
        queryFn: ({ pageParam }) => api.admin.satelliteContactSites.list({
            query: toValue(query),
            cursor: pageParam,
        }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.pagination.has_next
            ? lastPage.pagination.cursor ?? undefined
            : undefined,
        staleTime: 60_000,
        gcTime: 5 * 60_000,
    });

    watch(queryResult.error, (error) => {
        if (error) {
            toaster.create({
                title: "Failed to load satellite contact sites",
                type: "error",
            });
        }
    });

    return queryResult;
}

export function useSatelliteContactSite(siteId: MaybeRefOrGetter<number | string | null | undefined>) {
    const api = useApi();
    const normalizedSiteId = computed(() => toValue(siteId));

    return useQuery({
        queryKey: computed(() => [
            ...SATELLITE_CONTACT_SITES_QUERY_KEY,
            "detail",
            normalizedSiteId.value,
        ]),
        queryFn: () => api.admin.satelliteContactSites.get({
            params: { siteId: normalizedSiteId.value as number | string },
        }),
        enabled: computed(() => normalizedSiteId.value != null && normalizedSiteId.value !== ""),
        staleTime: 60_000,
        gcTime: 5 * 60_000,
    });
}

export function useSatelliteContactSitesStats() {
    const api = useApi();

    return useQuery({
        queryKey: [...SATELLITE_CONTACT_SITES_QUERY_KEY, "stats"],
        queryFn: () => api.admin.satelliteContactSites.stats(),
        staleTime: 60_000,
        gcTime: 5 * 60_000,
    });
}

export function useSatelliteContactSiteMutations() {
    const api = useApi();
    const queryClient = useQueryClient();
    const toaster = useToaster();

    const invalidateSatelliteContactSites = () => {
        queryClient.invalidateQueries({ queryKey: [...SATELLITE_CONTACT_SITES_QUERY_KEY] });
    };

    const createSite = useMutation({
        mutationFn: (body: CreateSatelliteContactSiteRequest) => api.admin.satelliteContactSites.create({ body }),
        onSuccess: () => {
            toaster.create({ title: "Satellite contact site created", type: "success" });
            invalidateSatelliteContactSites();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to create satellite contact site: ${error.message}`, type: "error" });
        },
    });

    const updateSite = useMutation({
        mutationFn: ({ siteId, body }: { siteId: number | string; body: UpdateSatelliteContactSiteRequest }) => (
            api.admin.satelliteContactSites.update({ params: { siteId }, body })
        ),
        onSuccess: () => {
            toaster.create({ title: "Satellite contact site updated", type: "success" });
            invalidateSatelliteContactSites();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to update satellite contact site: ${error.message}`, type: "error" });
        },
    });

    const deleteSite = useMutation({
        mutationFn: (siteId: number | string) => api.admin.satelliteContactSites.delete({ params: { siteId } }),
        onSuccess: () => {
            toaster.create({ title: "Satellite contact site deleted", type: "success" });
            invalidateSatelliteContactSites();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to delete satellite contact site: ${error.message}`, type: "error" });
        },
    });

    const regenerateKey = useMutation({
        mutationFn: (siteId: number | string) => api.admin.satelliteContactSites.regenerateKey({ params: { siteId } }),
        onSuccess: () => {
            toaster.create({ title: "Satellite contact key regenerated", type: "success" });
            invalidateSatelliteContactSites();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to regenerate satellite contact key: ${error.message}`, type: "error" });
        },
    });

    return {
        createSite,
        updateSite,
        deleteSite,
        regenerateKey,
    };
}
